import { Injectable } from '@angular/core';
import {
    ChapterType,
    IQuotationBlock,
    IQuotationSection,
    IQuotationsQuotation,
    IQuotationTraveller,
    IQuotationTravellerPrice
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { ITableBlock, ITableRow, TableRowType } from "./ITable";
import { Yii } from "../../../../libraries/interfaces/Yii";
import { QuotationBlockCategoryCodeEnum } from "libraries/interfaces/quotation-block-category-code.enum";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

export const BLOCK_PLACEHOLDER: ITableBlock = {
    isEmpty: true,
    isFirstSectionBlock: true,
    sectionLength: 1,
    order: 1,
    id: null,
    title: null,
    category: QuotationBlockCategoryCodeEnum.price,
    section: null,
};

export const tripChapterHeader: ITableRow = {
    title: Yii.t('wui-quotation-js', 'Voyage'),
    type: TableRowType.chapterHeader,
    chapterType: ChapterType.trip,
};

export const feesChapterHeader: ITableRow = {
    title: Yii.t('wui-quotation-js', 'Frais'),
    type: TableRowType.chapterHeader,
    chapterType: ChapterType.fees,
};

export const servicesChapterHeader: ITableRow = {
    title: Yii.t('wui-quotation-js', 'Services'),
    type: TableRowType.chapterHeader,
    chapterType: ChapterType.services,
};

@Injectable()
export class TableDataService {

    public hasCost(quotation: IQuotationsQuotation): boolean {
        return Boolean(quotation && quotation.cost && (quotation.cost.amount || quotation.cost.amount === 0));
    }

    public hasRetail(quotation: IQuotationsQuotation): boolean {
        return Boolean(quotation && quotation.retail);
    }

    public hasVat(quotation: IQuotationsQuotation): boolean {
        return Boolean(quotation && quotation.vat && quotation.vat.rate);
    }

    public hasRetailIncludingVat(quotation: IQuotationsQuotation): boolean {
        return Boolean(quotation && quotation.retailIncludingVat);
    }

    public hasRetailAndVat(quotation: IQuotationsQuotation): boolean {
        return this.hasRetail(quotation) && this.hasVat(quotation);
    }

    public extractHeaderTravellers(quotation: IQuotationsQuotation): IQuotationTraveller[] {
        return (quotation.travellers || [])
            .map((traveller: IQuotationTraveller) => {
                return {
                    id: traveller.id,
                    externalReference: traveller.externalReference,
                    firstName: traveller.firstName,
                    lastName: traveller.lastName,
                    travellerType: traveller.travellerType,
                };
            })
            .sort((a: IQuotationTraveller, b: IQuotationTraveller) => a.id - b.id);
    }

    public extractBlocksFromSection(quotation: IQuotationsQuotation, chapterType: ChapterType, subPage: EQuotationEntryPoints): ITableRow[] {
        return extractSection(quotation, chapterType)
            .sort((a: IQuotationSection, b: IQuotationSection) => a.order - b.order)
            .map((section: IQuotationSection) => {
                let sectionBlocks: IQuotationBlock[];
                switch (subPage) {
                    case EQuotationEntryPoints.option:
                        sectionBlocks = (section.blocks || []).filter((block: IQuotationBlock) => block.isOption);
                        break;
                    case EQuotationEntryPoints.cost:
                    default:
                        sectionBlocks = (section.blocks || []).filter((block: IQuotationBlock) => !block.isOption);
                }

                let isFirstSectionBlockWithCost: boolean = true;
                const blockWithCostQuantity: number = (sectionBlocks || [])
                    .filter((block: ITableBlock) => block.cost && (block.cost.amount || block.cost.amount === 0))
                    .length;

                return (sectionBlocks || [])
                    .sort((a: IQuotationBlock, b: IQuotationBlock) => a.order - b.order)
                    .map((block: ITableBlock, index: number, blocks: ITableBlock[]): ITableBlock => {
                        if (index === 0) {
                            block.isFirstSectionBlock = true;
                            block.sectionLength = blocks.length;
                        }
                        if (block.cost && (block.cost.amount || block.cost.amount === 0) && isFirstSectionBlockWithCost) {
                            block.isFirstSectionBlockWithCost = isFirstSectionBlockWithCost;
                            block.blockWithCostQuantity = blockWithCostQuantity;
                            isFirstSectionBlockWithCost = false;
                        }
                        if (block.beginOffset) {
                            block.quotationOffset = ((section.beginOffset || 0) - 1 + block.beginOffset);
                        }
                        if (block.travellerPrices && block.travellerPrices.length > 1) {
                            block.travellerPrices
                                .sort((a: IQuotationTravellerPrice, b: IQuotationTravellerPrice) => a.traveller - b.traveller);
                        }
                        return block;
                    });
            })
            .reduce((blocks: ITableBlock[], block: ITableBlock[]) => {
                return [...blocks, ...block];
            }, [])
            .map((block: ITableBlock) => {
                return {
                    type: TableRowType.blockData,
                    chapterType,
                    block,
                    travellers: extractBlockTravellers(quotation),
                };
            });
    }
}

function extractSection(quotation: IQuotationsQuotation, sectionType: ChapterType): IQuotationSection[] {
    return (quotation.sections || [])
        .filter((section: IQuotationSection) => Boolean(section.chapterCode && section.chapterCode.toLowerCase() === sectionType));
}

function extractBlockTravellers(quotation: IQuotationsQuotation): IQuotationTraveller[] {
    //tempo, will evolve to get quotation.blocks[0].prices[0].details[0].travellers[0].cost when possible
    return (quotation.travellers || []).map((traveller: IQuotationTraveller) => {
        return {
            id: traveller.id,
        };
    });
}

